import { useCallback, useEffect, useState } from "react";
import { StringHelper } from "@bryntum/schedulerpro";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EMPTY_FILTERS, hasStoredFilters, sanitizeStoredFilters, type CheckgridFilters } from "../utils/filters";

const SAVE_DELAY_MS = 400;

function readStored(userPreferences: ReturnType<typeof useSwivuBryntumContext>["userPreferences"]): Record<string, unknown> {
  const parsed = StringHelper.safeJsonParse(userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.checkgridFilters, "{}") ?? "{}");
  return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
}

/** Checklists view filters, remembered per board in the user's preferences (search text is not kept). */
export function usePersistedCheckgridFilters(boardId: number | null) {
  const { userPreferences } = useSwivuBryntumContext();
  const boardKey = boardId == null ? null : String(boardId);

  const load = (key: string | null): CheckgridFilters => (key ? sanitizeStoredFilters(readStored(userPreferences)[key]) : EMPTY_FILTERS);
  const [state, setState] = useState(() => ({ boardKey, filters: load(boardKey) }));

  // Switching boards swaps in that board's saved filters; never save one board's filters under another.
  if (state.boardKey !== boardKey) {
    setState({ boardKey, filters: load(boardKey) });
  }

  const setFilters = useCallback((filters: CheckgridFilters) => setState((prev) => ({ ...prev, filters })), []);

  const { filters } = state;
  useEffect(() => {
    if (!boardKey || !userPreferences || state.boardKey !== boardKey) return;
    const timer = window.setTimeout(() => {
      const { query: _query, ...persistable } = filters;
      const { [boardKey]: _previous, ...others } = readStored(userPreferences);
      const next = JSON.stringify(hasStoredFilters(filters) ? { ...others, [boardKey]: persistable } : others);
      // Skip when nothing changed (also covers the initial mount).
      if (next === JSON.stringify(readStored(userPreferences))) return;
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.checkgridFilters, next);
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [filters, boardKey, state.boardKey, userPreferences]);

  return [filters, setFilters] as const;
}
